import StandInForm from './StandInForm';
import { useEffect, useMemo, useState } from 'react';
import type { LobbyInfo } from '../../api/types';
import { useInvitePlayers } from '../../queries';
import { useConfig } from '../../store/config';
import { Button, Checkbox, Dialog, Field, StateBlock, TextInput } from '../../ui';
import { useToast } from '../../ui/toast';

/** Accepts comma, space or newline separated Steam IDs and keeps only digits. */
export function parseSteamIds(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\s,;]+/)
        .map((value) => value.trim())
        .filter((value) => /^\d{5,}$/.test(value)),
    ),
  );
}

export default function LobbyInviteDialog({
  lobby,
  onClose,
}: {
  lobby: LobbyInfo | null;
  onClose: () => void;
}) {
  const open = lobby !== null;
  const { config } = useConfig();
  const toast = useToast();
  const invite = useInvitePlayers();

  const [selected, setSelected] = useState<string[]>([]);
  const [manual, setManual] = useState('');

  useEffect(() => {
    if (!open) {
      setSelected([]);
      setManual('');
    }
  }, [open]);

  const steamIds = useMemo(
    () => Array.from(new Set([...selected, ...parseSteamIds(manual)])),
    [selected, manual],
  );

  const toggle = (steamId: string) =>
    setSelected((current) =>
      current.includes(steamId)
        ? current.filter((id) => id !== steamId)
        : [...current, steamId],
    );

  const submit = () => {
    if (!lobby || steamIds.length === 0) return;
    invite.mutate(
      { botId: lobby.bot_id, steamIds },
      {
        onSuccess: (result) => {
          const failed = result.failed_count ?? 0;
          if (failed > 0) {
            toast.push({
              tone: 'info',
              title: `Invited ${result.invited_count ?? steamIds.length}, ${failed} failed`,
            });
          } else {
            toast.success(`Invited ${result.invited_count ?? steamIds.length} player(s)`);
          }
          onClose();
        },
        onError: (error) => toast.error('Invite failed', (error as Error).message),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Invite players"
      description={lobby?.game_name}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={invite.isPending}
            disabled={steamIds.length === 0}
            onClick={submit}
          >
            Invite {steamIds.length > 0 ? `(${steamIds.length})` : ''}
          </Button>
        </>
      }
    >
      {config.players.length === 0 ? (
        <StateBlock
          title="No saved players"
          detail="Add players on the Players page, or paste Steam IDs below."
        />
      ) : (
        <div className="grid max-h-60 gap-1 overflow-y-auto rounded-md border border-line bg-surface-2/50 p-3 sm:grid-cols-2 scrollbar-slim">
          {config.players.map((player) => (
            <Checkbox
              key={player.steamId}
              label={player.name || player.steamId}
              checked={selected.includes(player.steamId)}
              onChange={() => toggle(player.steamId)}
            />
          ))}
        </div>
      )}

      <div className="mt-4">
        <Field label="Extra Steam IDs" hint="Separated by spaces, commas or new lines">
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              value={manual}
              onChange={(event) => setManual(event.target.value)}
              placeholder="76561198012345678, 76561198087654321"
            />
          )}
        </Field>
      </div>
      {lobby && lobby.controller_type !== 'series' && <details className="mt-4"><summary className="cursor-pointer text-sm">Approve a stand-in</summary><StandInForm botId={lobby.bot_id} /></details>}
      {lobby?.controller_type === 'series' && <p className="mt-4 text-sm text-ink-dim">Use Replace player in the Series panel to approve a stand-in before inviting them.</p>}
    </Dialog>
  );
}
